import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Gauge, RefreshCw, Loader2, AlertCircle, TrendingUp, TrendingDown,
  Minus, ShoppingCart, FileText, DollarSign, Wallet, Banknote, Clock,
  ChevronRight, ChevronDown, Table2, PiggyBank, Landmark, ArrowLeftRight,
  ArrowDownLeft, ArrowUpRight, Scale,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const BASE = `${API_URL}/api/admin/panel-diario`;

// Día calendario argentino, sin importar dónde esté el navegador.
const hoyAR = () =>
  new Date().toLocaleDateString('en-CA', { timeZone: 'America/Argentina/Buenos_Aires' });

const fmtMoney = (n) =>
  typeof n === 'number'
    ? `$${n.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : '—';

// Versión compacta para los números grandes ($279,6 M).
const fmtCorto = (n) => {
  if (typeof n !== 'number') return '—';
  const abs = Math.abs(n);
  if (abs >= 1e9) return `$${(n / 1e9).toLocaleString('es-AR', { maximumFractionDigits: 2 })} MM`;
  if (abs >= 1e6) return `$${(n / 1e6).toLocaleString('es-AR', { maximumFractionDigits: 1 })} M`;
  if (abs >= 1e3) return `$${(n / 1e3).toLocaleString('es-AR', { maximumFractionDigits: 1 })} K`;
  return fmtMoney(n);
};

const fmtMes = (ym) => {
  const [y, m] = String(ym).split('-').map(Number);
  if (!y || !m) return ym;
  const texto = new Date(y, m - 1, 1).toLocaleDateString('es-AR', { month: 'long', year: 'numeric' });
  return texto.charAt(0).toUpperCase() + texto.slice(1);
};

// Al elegir un mes, se va a su último día — salvo el mes en curso, que va a hoy.
const ultimoDiaDelMes = (ym) => {
  const hoy = hoyAR();
  if (ym === hoy.slice(0, 7)) return hoy;
  const [y, m] = String(ym).split('-').map(Number);
  return `${y}-${String(m).padStart(2, '0')}-${String(new Date(y, m, 0).getDate()).padStart(2, '0')}`;
};

const fmtFecha = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return y && m && d ? `${d}/${m}/${y}` : iso;
};

const fmtHora = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString('es-AR', {
      hour: '2-digit', minute: '2-digit', timeZone: 'America/Argentina/Buenos_Aires',
    });
  } catch { return '—'; }
};

const ICONO = {
  compras_oc: ShoppingCart,
  compras_facturas: FileText,
  ventas: DollarSign,
  cobros: Wallet,
  pagos: Banknote,
  fima: PiggyBank,
  financiacion: Landmark,
  ctas_propias: ArrowLeftRight,
  // No es un cobro ni un pago: la deuda se cancela de los dos lados a la vez.
  compensaciones: Scale,
};

const GRUPOS = [
  {
    key: 'compras',
    titulo: 'Compras',
    nota: 'Las órdenes de compra y las facturas no se suman entre sí: la OC de hoy se factura después. Una mide el compromiso asumido; la otra, el gasto devengado.',
  },
  { key: 'ventas', titulo: 'Ventas', nota: null },
  {
    key: 'cobranzas',
    titulo: 'Cobros y pagos',
    nota: 'El banco sale del extracto que se sube en Carga de Documentos. Son cobros y pagos de la operación: el fondo, los préstamos y los movimientos entre cuentas propias se miden abajo, en Tesorería.',
  },
  {
    key: 'tesoreria',
    titulo: 'Tesorería y financiación',
    nota: 'Plata que pasa por la cuenta pero no es cobro ni pago: cambia de lugar o es financiación. Cada tarjeta muestra lo que entró y lo que salió por separado, en el día y en el mes — acá no hay un total que valga la pena: lo que importa es la dirección.',
  },
];

// ── Chip de variación contra el mismo período del mes anterior ──
const Variacion = ({ concepto }) => {
  const { variacion_pct: v, mes_anterior_acum: base } = concepto;

  if (base === null || base === undefined) {
    return (
      <span className="text-[11px] text-gray-500">
        Sin base comparativa todavía
      </span>
    );
  }
  if (v === null || v === undefined) {
    return <span className="text-[11px] text-gray-500">{fmtCorto(base)} el mes pasado</span>;
  }

  const sube = v > 0;
  const plano = Math.abs(v) < 0.05;
  const Icon = plano ? Minus : sube ? TrendingUp : TrendingDown;
  const cls = plano
    ? 'bg-gray-500/15 text-gray-400 border-gray-500/30'
    : sube
      ? 'bg-green-500/15 text-green-400 border-green-500/30'
      : 'bg-red-500/15 text-red-400 border-red-500/30';

  return (
    <span className="flex items-center gap-1.5 flex-wrap">
      <span className={`inline-flex items-center gap-1 text-[10px] font-black px-2 py-0.5 rounded-full border ${cls}`}>
        <Icon className="w-3 h-3" />
        {plano ? '=' : `${sube ? '+' : ''}${v.toLocaleString('es-AR', { maximumFractionDigits: 1 })}%`}
      </span>
      <span className="text-[11px] text-gray-500">vs. {fmtCorto(base)}</span>
    </span>
  );
};

// ── Desglose de un concepto, línea por línea ──
// Sirve para los dos desgloses que hay: la apertura por categoría (cuenta
// contable en compras, tipo de comprobante en ventas) y las fuentes de un
// concepto (caja / banco).
//
// Se dibuja DOS veces, pegado al número del día y pegado al acumulado del mes.
// Antes aparecía una sola vez y siempre con el importe del día, así que el
// acumulado del mes quedaba sin explicar: se veía un total y no de dónde salía.
// `destacado` es para las tarjetas de tesorería, donde estas líneas no
// acompañan a un total: son el contenido de la tarjeta. Ahí van más grandes,
// con una flecha y color según entre o salga plata de la cuenta.
// Las líneas que NO suman al total del concepto, agrupadas. Hoy es el
// e-commerce de ventas: Mercado Libre y Web se muestran, pero sumarlas al
// número grande haría que la venta de los vendedores parezca más grande de lo
// que es. Se dibuja como una línea más, debajo del desglose que sí suma, con el
// total del grupo y desplegable — mismo criterio que los cheques en cartera.
const GrupoFueraDelTotal = ({ grupo, campo, abierto, onToggle }) => {
  const total = grupo.lineas.reduce((acc, l) => acc + (l[campo] || 0), 0);
  const Chevron = abierto ? ChevronDown : ChevronRight;
  return (
    <div className="mt-2 pt-2 border-t border-dashed border-gray-800">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-baseline justify-between gap-3 text-left group"
      >
        <span className="flex items-center gap-1 text-[11px] text-gray-500 group-hover:text-gray-300">
          <Chevron className="w-3 h-3 shrink-0" />
          {grupo.label}
        </span>
        <span className={`text-[11px] font-semibold tabular-nums ${total ? 'text-gray-400' : 'text-gray-600'}`}>
          {fmtMoney(total)}
        </span>
      </button>
      {abierto && <LineasDesglose lineas={grupo.lineas} campo={campo} />}
    </div>
  );
};

// Junta las líneas que quedan fuera del total por su `grupo`. Una línea sin
// grupo que igual no suma queda sola, en su propio bloque.
const agruparFueraDelTotal = (lineas) => {
  const mapa = new Map();
  (lineas || []).filter((l) => l.en_total === false).forEach((l) => {
    const key = l.grupo || l.key;
    if (!mapa.has(key)) mapa.set(key, { key, label: l.grupo_label || l.label, lineas: [] });
    mapa.get(key).lineas.push(l);
  });
  return [...mapa.values()];
};

const LineasDesglose = ({ lineas, campo, destacado = false }) => (
  <div className={destacado ? 'space-y-2' : 'mt-2 space-y-1 pl-3 border-l border-gray-800'}>
    {lineas.map((l) => {
      const estado = l.estado || 'ok';
      // "sin extracto" avisa que ese DÍA no tiene archivo cargado. El
      // acumulado del mes sí tiene dato, así que ahí va el importe.
      const avisaDelDia = campo === 'dia' && l.sin_archivo;
      const entra = (l.signo ?? 1) > 0;
      const valor = l[campo] || 0;
      const Flecha = entra ? ArrowDownLeft : ArrowUpRight;
      const colorImporte = !valor
        ? 'text-gray-600'
        : entra ? 'text-green-400' : 'text-amber-400';

      return (
        <div key={l.key} className="flex items-baseline justify-between gap-3">
          <span
            className={`flex items-center gap-1.5 text-[11px] ${
              estado !== 'ok' ? 'text-gray-600' : destacado ? 'text-gray-400' : 'text-gray-500'
            }`}
          >
            {destacado && (
              <Flecha className={`w-3 h-3 shrink-0 ${entra ? 'text-green-400' : 'text-amber-400'}`} />
            )}
            {l.label}
          </span>
          {estado !== 'ok' ? (
            <span
              className={`text-[10px] font-bold uppercase ${
                estado === 'error' ? 'text-red-400' : 'text-gray-600'
              }`}
            >
              {estado === 'error' ? 'error' : 'sin fuente'}
            </span>
          ) : avisaDelDia ? (
            <span
              className="text-[10px] font-bold uppercase text-yellow-500/90"
              title={l.mensaje || ''}
            >
              sin extracto
            </span>
          ) : (
            <span
              className={
                destacado
                  ? `text-base font-black tabular-nums ${colorImporte}`
                  : 'text-[11px] font-semibold text-gray-300 tabular-nums'
              }
            >
              {fmtMoney(valor)}
            </span>
          )}
        </div>
      );
    })}
  </div>
);

// ── Conceptos direccionales (tesorería) ──
// No llevan total. Sumar lo que entró con lo que salió no significa nada, y
// restarlo da un neto que tapa justo los dos números que se quieren ver.
const CuerpoDireccional = ({ lineas }) => (
  <>
    <div>
      <p className="text-[10px] font-bold uppercase text-gray-500 mb-2">Acumulado del mes</p>
      <LineasDesglose lineas={lineas} campo="mes_acum" destacado />
    </div>

    <div className="border-t border-gray-800 pt-3">
      <p className="text-[10px] font-bold uppercase text-gray-500 mb-1.5">Del día</p>
      <LineasDesglose lineas={lineas} campo="dia" />
    </div>
  </>
);

// ── Saldo FIMA (tarjeta "FIMA · fondo de inversión") ──
// Última captura "Inversiones en pesos" cargada en Carga de Documentos hasta la
// fecha del panel. Va arriba de los rescates/suscripciones del mes.
const fmtPct = (n) =>
  `${Math.abs(n).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;

const VariacionFima = ({ label, pct, monto }) => {
  if (typeof pct !== 'number' || typeof monto !== 'number') return null;
  const sube = monto >= 0;
  return (
    <span className="text-[11px] text-gray-500">
      {label}:{' '}
      <span className={`font-semibold ${sube ? 'text-emerald-400' : 'text-red-400'}`}>
        {sube ? '↑' : '↓'} {fmtPct(pct)} {fmtMoney(Math.abs(monto))}
      </span>
    </span>
  );
};

const SaldoFima = ({ saldo, conciliacion }) => (
  <div className="rounded-lg bg-[#00a1df]/10 border border-[#00a1df]/20 p-3">
    <p className="text-[10px] font-bold uppercase text-gray-400 mb-1">Saldo disponible</p>
    {saldo ? (
      <>
        <p className={`text-2xl font-black leading-none tabular-nums ${
          saldo.lectura_dudosa ? 'text-yellow-400' : 'text-white'
        }`}>{fmtMoney(saldo.saldo)}</p>
        {/* El saldo sale de leer una captura de pantalla. Cuando el control de
            coherencia no lo pudo validar hay que decirlo: el 27/10/2026 la
            tarjeta mostró $0,05 con la misma cara de siempre, y el número roto
            se leyó como un dato. */}
        {saldo.lectura_dudosa && (
          <p className="text-[10px] text-yellow-500/90 mt-1 leading-snug">
            No pude validar esta lectura contra la variación del día. Revisá el número
            contra la pantalla del fondo y, si no coincide, volvé a subir la captura.
          </p>
        )}
        <div className="flex flex-col gap-0.5 mt-1.5">
          <VariacionFima label="Diario" pct={saldo.diario_pct} monto={saldo.diario_monto} />
          <VariacionFima label="Acumulado" pct={saldo.acumulado_pct} monto={saldo.acumulado_monto} />
          <span className="text-[10px] text-gray-500">
            Al {fmtFecha(saldo.fecha)} {fmtHora(saldo.created_at)}
            {saldo.user_name ? ` · cargó ${saldo.user_name}` : ''}
          </span>
        </div>

        {/* El saldo sale de un OCR, pero el movimiento del fondo pasa por el
            banco: saldo final = saldo inicial − rescates + suscripciones +
            rendimiento. Si el rendimiento implícito no es plausible, alguno de
            los tres datos está mal y la tarjeta lo dice en vez de presentar el
            número como verificado. */}
        {/* Sin datos para conciliar también se dice. Que la tarjeta no muestre
            nada es indistinguible de "está todo bien", y eso es justo lo que
            estamos tratando de sacar del panel. */}
        {conciliacion && !conciliacion.hay_datos && (
          <p className="text-[10px] text-gray-500 mt-2 leading-snug">
            {conciliacion.avisos?.[0] || 'Todavía no se puede verificar contra el banco.'}
          </p>
        )}
        {conciliacion?.hay_datos && (
          conciliacion.cierra ? (
            <p className="text-[10px] text-emerald-400/90 mt-2 leading-snug">
              Verificado contra el banco: del {fmtFecha(conciliacion.desde)} al{' '}
              {fmtFecha(conciliacion.hasta)} rindió {fmtMoney(conciliacion.rendimiento_implicito)}
              {conciliacion.rescates ? `, con ${fmtCorto(conciliacion.rescates)} de rescates` : ''}
              {conciliacion.suscripciones ? ` y ${fmtCorto(conciliacion.suscripciones)} de suscripciones` : ''}.
            </p>
          ) : (
            <div className="mt-2 space-y-1">
              {conciliacion.avisos.map((a) => (
                <p key={a} className="text-[10px] text-yellow-500/90 leading-snug">{a}</p>
              ))}
              <p className="text-[10px] text-gray-600 leading-snug">
                Saldo anterior {fmtCorto(conciliacion.saldo_inicial)} · rescates{' '}
                {fmtCorto(conciliacion.rescates)} · suscripciones{' '}
                {fmtCorto(conciliacion.suscripciones)} → rendimiento implícito{' '}
                {fmtMoney(conciliacion.rendimiento_implicito)}.
              </p>
            </div>
          )
        )}
      </>
    ) : (
      <p className="text-xs text-gray-500">
        Sin saldo cargado. Se carga con la captura de "Inversiones en pesos" en Carga de Documentos.
      </p>
    )}
  </div>
);

// ── Cartera: deuda a cobrar y cheques en cartera ──
// No es un concepto más del panel. Los demás miden FLUJO —cuánto se facturó
// hoy, cuánto se acumuló en el mes— y este mide un SALDO: una foto a una fecha.
// Por eso no tiene "del día" ni comparativo contra el mes anterior.
const TRAMOS_ORDEN = ['1-30', '31-60', '61-90', '91-180', '181-365', '+365'];

const ALERTAS_CARTERA = [
  ['contado_sin_cobrar', 'Contado o anticipado sin cobrar'],
  ['condicion_desalineada', 'Vencimiento que no coincide con la condición'],
  // Las tres del cruce entre el banco y YiQi. El saldo es el del banco: esto
  // dice qué hay que arreglar en YiQi, no mueve un peso del total.
  ['cheques_sin_cargar_en_yiqi', 'En el banco y sin cargar en YiQi'],
  ['cheques_que_el_banco_no_tiene', 'En YiQi y el banco ya no los tiene'],
  ['cheques_con_importe_distinto', 'Mismo cheque con importe distinto'],
  ['cheques_olvidados', 'Cheques pasados de fecha hace más de 30 días'],
  ['cheques_sin_imputar_vencidos', 'Cheques sin imputar y pasados de fecha'],
  ['condicion_sospechosa', 'Clientes con patrón de condición mal cargada'],
];

// A partir de cuántos días sin cargar los listados del banco la tarjeta avisa.
// Tres deja pasar el fin de semana sin molestar y hace notar un lunes sin carga.
const DIAS_ARCHIVO_VIEJO = 3;

const diasDesde = (iso) => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? null : Math.floor((Date.now() - t) / 86400000);
};

// Cómo se llaman los dos listados del banco cuando hay que pedirlos. Tienen que
// decirse IGUAL que en Carga de Documentos: el mensaje manda a buscarlos ahí, y
// además hay otra clase parecida —"Cheques recibidos", el PDF diario de
// tesorería— que no tiene nada que ver con esto.
const ETIQUETA_LISTADO = {
  cheques_echeq: 'Cartera · eCheqs del banco',
  cheques_custodia: 'Cartera · cheques físicos',
};

// Qué columnas se muestran al abrir una alerta. Es una lista BLANCA a
// propósito: las filas del snapshot traen campos internos (clave, cliente_id,
// estado_codigo, banderas booleanas) que no le dicen nada a nadie, y además
// cada alerta trae filas de una forma distinta —facturas, cheques de YiQi,
// cheques del banco, clientes agrupados—. Recorrer las claves de la primera
// fila daría una tabla de quince columnas que cambia sola cada vez que el
// backend agrega un campo.
const ORDEN_CAMPOS = [
  'cliente', 'cuit', 'vendedor', 'numero', 'recibo', 'banco', 'punto_venta',
  'condicion', 'condicion_cliente', 'estado', 'emision', 'vencimiento',
  'fecha_recibo', 'fecha_pago', 'fecha_cobro', 'plazo_declarado',
  'plazo_cargado', 'plazo_sistema', 'dias_vencido', 'dias_para_cobro',
  'exceso', 'exceso_promedio', 'exceso_min', 'exceso_max', 'cheques',
  'cantidad_facturas', 'senal', 'importe_yiqi', 'diferencia', 'total',
  'pendiente', 'importe',
];

const ETIQUETA_CAMPO = {
  cliente: 'Cliente', cuit: 'CUIT', vendedor: 'Vendedor', numero: 'Número',
  recibo: 'Recibo', banco: 'Banco', punto_venta: 'Punto de venta',
  condicion: 'Condición', condicion_cliente: 'Condición del cliente',
  estado: 'Estado', emision: 'Emisión', vencimiento: 'Vencimiento',
  fecha_recibo: 'Fecha recibo', fecha_pago: 'Fecha de pago',
  fecha_cobro: 'Fecha de cobro', plazo_declarado: 'Plazo declarado',
  plazo_cargado: 'Plazo cargado', plazo_sistema: 'Plazo en sistema',
  dias_vencido: 'Días vencido', dias_para_cobro: 'Días para cobro',
  exceso: 'Exceso', exceso_promedio: 'Exceso prom.', exceso_min: 'Exceso mín.',
  exceso_max: 'Exceso máx.', cheques: 'Cheques',
  cantidad_facturas: 'Facturas', senal: 'Señal', importe_yiqi: 'Importe YiQi',
  diferencia: 'Diferencia', total: 'Total', pendiente: 'Pendiente',
  importe: 'Importe',
};

const CAMPOS_MONEY = new Set([
  'pendiente', 'importe', 'total', 'importe_yiqi', 'diferencia',
]);
const CAMPOS_FECHA = new Set([
  'emision', 'vencimiento', 'fecha_recibo', 'fecha_pago', 'fecha_cobro',
]);

const columnasDe = (filas) => {
  const presentes = new Set();
  filas.forEach((f) => Object.keys(f).forEach((k) => {
    if (f[k] !== null && f[k] !== undefined && f[k] !== '') presentes.add(k);
  }));
  return ORDEN_CAMPOS.filter((k) => presentes.has(k));
};

const valorCampo = (fila, k) => {
  const v = fila[k];
  if (v === null || v === undefined || v === '') return '—';
  if (CAMPOS_MONEY.has(k)) return fmtMoney(v);
  if (CAMPOS_FECHA.has(k)) return fmtFecha(v);
  return String(v);
};

// Agrupado por vendedor porque así se reparte el trabajo de arreglarlo: cada
// vendedor se lleva su lista. Sin vendedor resuelto van juntas al final, que es
// un problema del cruce y no de nadie en particular.
const porVendedor = (filas) => {
  const mapa = new Map();
  filas.forEach((f) => {
    const v = f.vendedor || 'Sin vendedor';
    if (!mapa.has(v)) mapa.set(v, []);
    mapa.get(v).push(f);
  });
  const campo = (f) => f.pendiente ?? f.importe ?? 0;
  return [...mapa.entries()]
    .map(([vendedor, fs]) => ({
      vendedor,
      filas: fs,
      importe: fs.reduce((a, f) => a + (campo(f) || 0), 0),
    }))
    .sort((a, b) => b.importe - a.importe);
};

const aMarkdown = (titulo, corte, grupos, columnas) => {
  const lineas = [`# ${titulo}`, '', `Cartera al ${fmtFecha(corte)}.`, ''];
  grupos.forEach((g) => {
    lineas.push(`## ${g.vendedor} — ${fmtMoney(g.importe)} (${g.filas.length})`, '');
    lineas.push(`| ${columnas.map((k) => ETIQUETA_CAMPO[k] || k).join(' | ')} |`);
    lineas.push(`| ${columnas.map(() => '---').join(' | ')} |`);
    g.filas.forEach((f) => {
      lineas.push(`| ${columnas.map((k) => valorCampo(f, k)).join(' | ')} |`);
    });
    lineas.push('');
  });
  return lineas.join('\n');
};

const bajarArchivo = (nombre, texto) => {
  const url = URL.createObjectURL(new Blob([texto], { type: 'text/markdown;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = nombre;
  a.click();
  URL.revokeObjectURL(url);
};

// Una alerta, desplegable. El detalle se pide SOLO al abrirla: son miles de
// filas que casi nunca se miran, y por eso la foto guarda nada más que la
// cantidad y el importe.
const AlertaCartera = ({ clave, label, alerta, corte, token }) => {
  const [abierto, setAbierto] = useState(false);
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!abierto || data) return;
    let vigente = true;
    (async () => {
      setCargando(true); setError(null);
      try {
        const q = corte ? `?corte=${encodeURIComponent(corte)}` : '';
        const res = await fetch(`${BASE}/cartera/alerta/${encodeURIComponent(clave)}${q}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const body = await res.json();
        if (!res.ok) throw new Error(body.detail || `Error ${res.status}`);
        if (vigente) setData(body);
      } catch (err) {
        if (vigente) setError(err.message);
      } finally {
        if (vigente) setCargando(false);
      }
    })();
    return () => { vigente = false; };
  }, [abierto, data, clave, corte, token]);

  const filas = data?.filas || [];
  const columnas = filas.length ? columnasDe(filas) : [];
  const grupos = filas.length ? porVendedor(filas) : [];
  const md = () => aMarkdown(label, data?.corte, grupos, columnas);

  return (
    <div>
      <button
        onClick={() => setAbierto((v) => !v)}
        className="w-full flex items-baseline justify-between gap-3 text-left hover:bg-white/[0.02] rounded px-1 -mx-1 py-0.5 transition-colors"
        aria-expanded={abierto}
      >
        <span className="text-[11px] text-yellow-500/80 flex items-center gap-1.5">
          <ChevronRight className={`w-3 h-3 shrink-0 text-gray-600 transition-transform ${abierto ? 'rotate-90' : ''}`} />
          <AlertCircle className="w-3 h-3 shrink-0" />
          {label}
        </span>
        <span className="text-[11px] text-gray-400 tabular-nums shrink-0">
          {alerta.cantidad}{alerta.importe ? ` · ${fmtCorto(alerta.importe)}` : ''}
        </span>
      </button>

      {abierto && (
        <div className="mt-2 mb-3 ml-4 pl-3 border-l border-gray-800 space-y-3">
          {cargando && (
            <p className="flex items-center gap-2 text-[11px] text-[#00a1df] font-semibold">
              <Loader2 className="w-3 h-3 animate-spin" /> Buscando el detalle…
            </p>
          )}
          {error && (
            <p className="flex items-center gap-2 text-[11px] text-red-400">
              <AlertCircle className="w-3 h-3" /> {error}
            </p>
          )}
          {data?.avisos?.map((a) => (
            <p key={a} className="text-[10px] text-yellow-500/80 leading-snug">{a}</p>
          ))}
          {data && !cargando && !filas.length && (
            <p className="text-[11px] text-gray-500">
              No pude rearmar el detalle de esta alerta.
            </p>
          )}
          {!!filas.length && (
            <>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(md())
                      .then(() => toast.success('Detalle copiado'))
                      .catch(() => toast.error('No pude copiar al portapapeles'));
                  }}
                  className="text-[10px] font-bold px-2 py-1 rounded border border-gray-700 text-gray-300 hover:border-gray-500"
                >
                  Copiar
                </button>
                <button
                  onClick={() => bajarArchivo(`${clave}-${data.corte}.md`, md())}
                  className="text-[10px] font-bold px-2 py-1 rounded border border-gray-700 text-gray-300 hover:border-gray-500"
                >
                  Descargar .md
                </button>
                <span className="text-[10px] text-gray-600">
                  {filas.length} fila(s) en {grupos.length} vendedor(es)
                </span>
              </div>

              {grupos.map((g) => (
                <div key={g.vendedor} className="space-y-1">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                    {g.vendedor}
                    <span className="ml-2 font-normal normal-case tracking-normal text-gray-600">
                      {g.filas.length} · {fmtCorto(g.importe)}
                    </span>
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-[10px]">
                      <thead>
                        <tr className="text-gray-500 border-b border-gray-800">
                          {columnas.map((k) => (
                            <th key={k} className="py-1 pr-3 text-left font-semibold whitespace-nowrap">
                              {ETIQUETA_CAMPO[k] || k}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {g.filas.map((f, i) => (
                          <tr key={f.clave || i} className="border-b border-gray-900 text-gray-400">
                            {columnas.map((k) => (
                              <td
                                key={k}
                                className={`py-1 pr-3 whitespace-nowrap ${
                                  CAMPOS_MONEY.has(k) ? 'tabular-nums text-gray-300' : ''
                                }`}
                              >
                                {valorCampo(f, k)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const FilaCartera = ({ label, valor, detalle, destacado }) => (
  <div className="flex items-baseline justify-between gap-3">
    <span className={`text-xs ${destacado ? 'text-gray-300' : 'text-gray-500'}`}>
      {label}
      {detalle != null && <span className="text-gray-600"> · {detalle}</span>}
    </span>
    <span
      className={`tabular-nums ${
        destacado ? 'text-sm font-bold text-white' : 'text-xs text-gray-400'
      }`}
    >
      {fmtMoney(valor)}
    </span>
  </div>
);

// Bloque que vive CERRADO: el encabezado muestra el total y el detalle se abre
// solo si alguien lo pide. La tarjeta tiene mucha información adentro y casi
// siempre lo que se mira es el número de arriba.
const BloqueCartera = ({ titulo, total, detalle, children }) => {
  const [abierto, setAbierto] = useState(false);
  return (
    <div className="rounded-lg border border-gray-800 overflow-hidden">
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-white/[0.02] transition-colors"
        aria-expanded={abierto}
      >
        <ChevronRight
          className={`w-3.5 h-3.5 text-gray-500 shrink-0 transition-transform ${abierto ? 'rotate-90' : ''}`}
        />
        <span className="text-[10px] font-bold uppercase text-gray-400">{titulo}</span>
        <span className="ml-auto text-sm font-bold text-white tabular-nums">{fmtMoney(total)}</span>
        {detalle && <span className="text-[10px] text-gray-500 shrink-0">{detalle}</span>}
      </button>
      {abierto && <div className="px-3 pb-3 pt-1 space-y-1.5 border-t border-gray-800">{children}</div>}
    </div>
  );
};

const TarjetaCartera = ({ cartera, token }) => {
  // `null` es un fallo leyendo la foto; `hay_datos:false` es que todavía no hay
  // ninguna. Las dos se muestran igual, pero ninguna muestra ceros: un cero se
  // leería como "no hay deuda", que es exactamente lo contrario de un hueco.
  if (!cartera || !cartera.hay_datos) {
    return (
      <div className="bg-[#1A1D27] rounded-xl border border-gray-800 shadow-xl p-5 flex flex-col gap-3 opacity-60">
        <div className="flex items-center gap-2.5">
          <span className="p-2 rounded-lg bg-[#00a1df]/10"><Wallet className="w-4 h-4 text-[#00a1df]" /></span>
          <h3 className="text-[13px] font-black text-white">Deuda a cobrar</h3>
        </div>
        <p className="text-xs text-gray-500">
          Todavía no hay ninguna foto de la cartera. La toma el job diario;
          hasta entonces no se muestra ningún número.
        </p>
      </div>
    );
  }

  const { resumen, alertas, escalonados, corte } = cartera;
  const d = resumen.deuda;
  const ch = resumen.cheques;
  // Las fotos tomadas antes de que existieran los saldos a favor no traen el
  // bloque. En ese caso no se dibuja: mostrar $0 sería afirmar que no hay
  // saldos, y lo que pasa es que esa foto no los midió.
  const sal = resumen.saldos_a_favor;
  // De dónde salió el saldo de cheques. Sin esto no se puede distinguir "no hay
  // cheques" de "nadie cargó los archivos", que son cosas muy distintas.
  const banco = cartera.banco;
  const sinArchivos = banco && !banco.completo;
  const diasArchivo = diasDesde(banco?.cargado_at);
  const activa = d.activa;
  const vencida = activa.vencida;
  const inmediata = vencida.de_condicion_inmediata;
  const avisos = ALERTAS_CARTERA
    .map(([clave, label]) => [clave, label, alertas?.[clave]])
    .filter(([, , a]) => a && a.cantidad > 0);

  return (
    <div className="bg-[#1A1D27] rounded-xl border border-gray-800 shadow-xl p-5 flex flex-col gap-4 hover:border-[#00a1df]/40 transition-colors md:col-span-2">
      <div className="flex items-center gap-2.5">
        <span className="p-2 rounded-lg bg-[#00a1df]/10"><Wallet className="w-4 h-4 text-[#00a1df]" /></span>
        <h3 className="text-[13px] font-black text-white">Deuda a cobrar</h3>
        {/* Con la HORA, no solo la fecha. Un cheque se endosa y se entrega a un
            proveedor en cualquier momento del día: el 22/10/2026 la cartera
            pasó de $548 a $498 millones entre el mediodía y las cuatro de la
            tarde. Sin la hora, este número no se puede comparar contra nada. */}
        <span className="ml-auto text-[10px] text-gray-500">
          Al {fmtFecha(corte)}
          {cartera.actualizado_at ? ` ${fmtHora(cartera.actualizado_at)}` : ''}
        </span>
      </div>

      {/* Los dos números van SEPARADOS y no se suman. Una factura pendiente y un
          cheque a 90 días son dos cosas distintas: una todavía hay que cobrarla,
          la otra ya se cobró pero la plata no entró. Sumarlas da un número que
          no responde ninguna pregunta. */}
      <div className={`grid grid-cols-1 gap-3 ${sal ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>
        <div className="rounded-lg bg-[#00a1df]/10 border border-[#00a1df]/20 p-3">
          <p className="text-[10px] font-bold uppercase text-gray-400 mb-1">Pendiente de cobro</p>
          <p className="text-2xl font-black leading-none text-white tabular-nums">{fmtMoney(d.importe)}</p>
          <p className="text-[10px] text-gray-500 mt-1.5">{d.facturas} facturas emitidas sin cancelar</p>
        </div>
        <div className="rounded-lg bg-white/[0.03] border border-gray-800 p-3">
          <p className="text-[10px] font-bold uppercase text-gray-400 mb-1">Cheques en cartera</p>
          {sinArchivos ? (
            <>
              <p className="text-2xl font-black leading-none text-gray-600 tabular-nums">—</p>
              <p className="text-[10px] text-yellow-500/90 mt-1.5">
                Falta cargar en Carga de Documentos:{' '}
                {(banco.faltan || []).map((f) => ETIQUETA_LISTADO[f] || f).join(' y ')}.
                Sin los dos listados el saldo saldría corto.
              </p>
            </>
          ) : (
            <>
              <p className="text-2xl font-black leading-none text-white tabular-nums">{fmtMoney(ch.importe)}</p>
              {/* De DÓNDE salió este saldo. Una foto anterior a los archivos
                  del banco no tiene el campo `banco`, y decir "según el banco"
                  ahí es afirmar algo falso sobre el origen del número: esos
                  cheques son los de YiQi. */}
              <p className="text-[10px] text-gray-500 mt-1.5">
                {ch.cheques} cheques {banco ? 'según el banco' : 'según YiQi'}
              </p>
              {!banco && (
                <p className="text-[10px] text-yellow-500/90 mt-0.5">
                  Foto anterior a los listados del banco. El saldo pasa a ser el
                  de los archivos en la próxima captura de la cartera.
                </p>
              )}
              {/* Sello propio: el saldo de cheques es del archivo, no del día
                  del panel. Un domingo muestra el del viernes, y tiene que
                  decirlo — si no, se lee como si fuera de hoy. */}
              {banco?.cargado_at && (
                <p className="text-[10px] text-gray-500 mt-0.5">
                  Archivos del {fmtFecha(banco.cargado_at)} {fmtHora(banco.cargado_at)}
                </p>
              )}
              {diasArchivo != null && diasArchivo >= DIAS_ARCHIVO_VIEJO && (
                <p className="text-[10px] text-yellow-500/90 mt-0.5">
                  Hace {diasArchivo} días que no se cargan los listados del banco.
                </p>
              )}
            </>
          )}
        </div>
        {sal && (
          <div className="rounded-lg bg-white/[0.03] border border-gray-800 p-3">
            <p className="text-[10px] font-bold uppercase text-gray-400 mb-1">Saldos a favor sin imputar</p>
            <p className="text-2xl font-black leading-none text-white tabular-nums">{fmtMoney(sal.importe)}</p>
            <p className="text-[10px] text-gray-500 mt-1.5">
              {sal.clientes} clientes que ya pagaron y no se aplicó a ninguna factura
            </p>
          </div>
        )}
      </div>

      {sal && sal.clientes > 0 && (
        <BloqueCartera titulo="Saldos a favor por vendedor" total={sal.importe}
                       detalle={`${sal.clientes} clientes`}>
          {(sal.por_vendedor || []).map((v) => (
            <FilaCartera key={v.vendedor} label={v.vendedor} valor={v.importe}
                         detalle={`${v.clientes} cli.`} />
          ))}
          <p className="text-[10px] text-gray-600 pt-1 leading-snug">
            Esta plata ya entró: no está en el pendiente de cobro ni se suma a él.
            La resuelve el vendedor, que es quien sabe contra qué factura va.
          </p>
        </BloqueCartera>
      )}

      <BloqueCartera titulo="Cartera activa" total={activa.importe}
                     detalle={`${activa.facturas} fact.`}>
        <FilaCartera label="A vencer" valor={activa.a_vencer.importe}
                     detalle={`${activa.a_vencer.facturas} fact.`} destacado />
        <FilaCartera label="Vencida" valor={vencida.importe}
                     detalle={`${vencida.facturas} fact.`} destacado />
        {TRAMOS_ORDEN.map((t) => {
          const tramo = vencida.tramos?.[t];
          if (!tramo || !tramo.facturas) return null;
          return <FilaCartera key={t} label={t} valor={tramo.importe} detalle={`${tramo.facturas}`} />;
        })}
        {inmediata.facturas > 0 && (
          <p className="text-[10px] text-gray-600 pt-1 leading-snug">
            De lo vencido, {fmtCorto(inmediata.importe)} son facturas de contado o
            anticipado: vencen el mismo día que se emiten.
          </p>
        )}
        {d.judicial.facturas > 0 && (
          <div className="pt-1.5 border-t border-gray-800 mt-1.5">
            <FilaCartera label="En gestión judicial" valor={d.judicial.importe}
                         detalle={`${d.judicial.facturas} fact.`} />
          </div>
        )}
      </BloqueCartera>

      <BloqueCartera titulo="Cheques en cartera" total={ch.importe}
                     detalle={`${ch.cheques} chq.`}>
        {Object.entries(ch.por_mes || {}).map(([mes, slot]) => (
          <FilaCartera key={mes} label={fmtMes(mes)} valor={slot.importe}
                       detalle={`${slot.cheques} chq.`} />
        ))}
        {ch.pasados_de_fecha.cheques > 0 && (
          <FilaCartera label="Pasados de fecha" valor={ch.pasados_de_fecha.importe}
                       detalle={`${ch.pasados_de_fecha.cheques} chq.`} />
        )}
        {ch.plazo_ponderado != null && (
          <p className="text-[10px] text-gray-600 pt-1 leading-snug">
            Se vende a {ch.plazo_ponderado} días y se cobra a{' '}
            {ch.plazo_ponderado + ch.exceso_ponderado}: {ch.exceso_ponderado} días de exceso,
            ponderado por importe.
          </p>
        )}
        {escalonados?.cantidad > 0 && (
          <p className="text-[10px] text-gray-600 leading-snug">
            Incluye {escalonados.cantidad} pagos en cuotas pactadas por{' '}
            {fmtCorto(escalonados.importe)}, que no cuentan como mora.
          </p>
        )}
      </BloqueCartera>

      {/* Las alertas marcan un problema DENTRO de los números de arriba: no
          suman ni restan de ningún total. */}
      {avisos.length > 0 && (
        <div className="pt-3 border-t border-gray-800 space-y-1">
          {avisos.map(([clave, label, a]) => (
            <AlertaCartera key={clave} clave={clave} label={label} alerta={a}
                           corte={corte} token={token} />
          ))}
          <p className="text-[10px] text-gray-600 pt-1">
            Las alertas señalan filas que ya están contadas arriba: no suman ni restan de ningún
            total. Tocá una para ver el detalle, agrupado por vendedor.
          </p>
        </div>
      )}
    </div>
  );
};

// ── Tarjeta de un concepto ──
const ESTADO_BADGE = {
  parcial: { texto: 'Parcial', cls: 'border-yellow-500/40 text-yellow-400' },
  pendiente: { texto: 'Pendiente', cls: 'border-gray-600 text-gray-400' },
};

const Tarjeta = ({ concepto }) => {
  const Icon = ICONO[concepto.key] || Gauge;
  const pendiente = concepto.estado === 'pendiente';
  const parcial = concepto.estado === 'parcial';
  const error = concepto.estado === 'error';
  const badge = ESTADO_BADGE[concepto.estado];
  const componentes = concepto.componentes || [];
  // Qué desglosar debajo de cada número, en orden de precisión:
  //   lineas    una por contraparte, con nombre (tesorería)
  //   apertura  por categoría: cuenta contable en compras, tipo A / X en ventas
  //   fuentes   caja y banco, cuando el concepto tiene más de una
  // Una sola fuente no se desglosa: repetiría el mismo importe.
  const todas =
    concepto.lineas || concepto.apertura || (componentes.length > 1 ? componentes : null);
  // Las que suman al número grande van pegadas a él; las que no, en un bloque
  // aparte. Si se mezclaran, el desglose no cerraría con el total y la tarjeta
  // entera dejaría de ser creíble.
  const lineas = todas ? todas.filter((l) => l.en_total !== false) : null;
  const grupos = agruparFueraDelTotal(todas);
  // Un solo estado para los dos desgloses: que el mes y el día se abran juntos.
  const [gruposAbiertos, setGruposAbiertos] = useState({});
  const alternarGrupo = (key) =>
    setGruposAbiertos((prev) => ({ ...prev, [key]: !prev[key] }));
  // Días del mes sin extracto cargado. Es el aviso más importante de la
  // tarjeta: el acumulado es el número grande y si le faltan días está
  // incompleto, sin que se note en ninguna parte.
  const faltanMes = [
    ...new Set(componentes.flatMap((c) => c.dias_mes_sin_extracto || [])),
  ].sort();

  return (
    <div
      className={`bg-[#1A1D27] rounded-xl border shadow-xl p-5 flex flex-col gap-4 transition-colors ${
        error ? 'border-red-500/40'
          : pendiente ? 'border-gray-800 opacity-60'
          : parcial ? 'border-yellow-500/25 hover:border-yellow-500/40'
          : 'border-gray-800 hover:border-[#00a1df]/40'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className={`p-2 rounded-lg shrink-0 ${pendiente ? 'bg-gray-700/30' : 'bg-[#00a1df]/15'}`}>
            <Icon className={`w-4 h-4 ${pendiente ? 'text-gray-500' : 'text-[#00a1df]'}`} />
          </div>
          <h3 className="text-sm font-black text-white leading-tight">{concepto.label}</h3>
        </div>
        {badge && (
          <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded-full border shrink-0 ${badge.cls}`}>
            {badge.texto}
          </span>
        )}
      </div>

      {concepto.key === 'fima' && 'saldo_fima' in concepto && (
        <SaldoFima saldo={concepto.saldo_fima}
                   conciliacion={concepto.fima_conciliacion} />
      )}

      {error ? (
        <div className="flex items-start gap-2 text-xs text-red-400 bg-red-500/10 rounded-lg p-3">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <span className="break-words">{concepto.mensaje || 'No se pudo leer la fuente.'}</span>
        </div>
      ) : (
        <>
          {concepto.direccional && !pendiente && lineas ? (
            <CuerpoDireccional lineas={lineas} />
          ) : (
            <>
              <div className="flex flex-col gap-1.5">
                <p className="text-[10px] font-bold uppercase text-gray-500">Acumulado del mes</p>
                <p className={`text-3xl font-black leading-none ${pendiente ? 'text-gray-600' : 'text-white'}`}>
                  {pendiente ? '—' : fmtMoney(concepto.mes_acum)}
                </p>
                {!pendiente && lineas && lineas.length > 0 && (
                  <LineasDesglose lineas={lineas} campo="mes_acum" />
                )}
                {!pendiente && grupos.map((g) => (
                  <GrupoFueraDelTotal
                    key={g.key}
                    grupo={g}
                    campo="mes_acum"
                    abierto={!!gruposAbiertos[g.key]}
                    onToggle={() => alternarGrupo(g.key)}
                  />
                ))}
                {!pendiente && <Variacion concepto={concepto} />}
              </div>

              <div className="border-t border-gray-800 pt-3">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-[10px] font-bold uppercase text-gray-500">Del día</span>
                  <span className={`text-sm font-bold tabular-nums ${pendiente ? 'text-gray-600' : 'text-gray-200'}`}>
                    {pendiente ? '—' : fmtMoney(concepto.dia)}
                  </span>
                </div>
                {!pendiente && lineas && lineas.length > 0 && (
                  <LineasDesglose lineas={lineas} campo="dia" />
                )}
                {!pendiente && grupos.map((g) => (
                  <GrupoFueraDelTotal
                    key={g.key}
                    grupo={g}
                    campo="dia"
                    abierto={!!gruposAbiertos[g.key]}
                    onToggle={() => alternarGrupo(g.key)}
                  />
                ))}
              </div>
            </>
          )}

          {faltanMes.length > 0 && (
            <div className="flex items-start gap-2 text-[11px] text-yellow-500/90 bg-yellow-500/10 rounded-lg p-2.5">
              <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-px" />
              <span>
                <strong className="font-black">
                  El acumulado del mes está incompleto:
                </strong>{' '}
                faltan {faltanMes.length} día(s) de extracto bancario
                {' '}({faltanMes.slice(0, 4).map((d) => d.slice(8) + '/' + d.slice(5, 7)).join(', ')}
                {faltanMes.length > 4 ? ` y ${faltanMes.length - 4} más` : ''}).
                Súbilos en Carga de Documentos.
              </span>
            </div>
          )}

          {componentes.some((c) => c.sin_archivo) && (
            <div className="flex items-start gap-2 text-[11px] text-yellow-500/90 bg-yellow-500/10 rounded-lg p-2.5">
              <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-px" />
              <span>
                Todavía no se cargó el extracto bancario de este día, así que el banco
                figura en cero. No es lo mismo que un día sin movimientos.
              </span>
            </div>
          )}

          {parcial && concepto.mensaje && (
            <div className="flex items-start gap-2 text-[11px] text-yellow-500/90 bg-yellow-500/10 rounded-lg p-2.5">
              <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-px" />
              <span>{concepto.mensaje}. El total todavía no es el del día completo.</span>
            </div>
          )}
        </>
      )}

      <p className="text-[11px] text-gray-500 leading-snug border-t border-gray-800/60 pt-3">
        {concepto.detalle}
      </p>
    </div>
  );
};

const Esqueleto = () => (
  <div className="bg-[#1A1D27] rounded-xl border border-gray-800 p-5 animate-pulse flex flex-col gap-4">
    <div className="h-4 w-2/3 bg-gray-700/50 rounded" />
    <div className="h-8 w-1/2 bg-gray-700/50 rounded" />
    <div className="h-3 w-full bg-gray-700/30 rounded" />
    <div className="h-3 w-4/5 bg-gray-700/30 rounded" />
  </div>
);

// ── Desplegable de verificación ─────────────────────────────────────────────
// Vive cerrado. Muestra los registros que componen cada total: el total al pie
// de cada tabla tiene que dar exactamente el acumulado del mes de la tarjeta.
const celda = (col, fila) => {
  const v = fila[col.key];
  if (col.tipo === 'importe') return fmtMoney(v);
  if (col.tipo === 'fecha') return fmtFecha(v);
  return v || '—';
};

const TablaFuente = ({ fuente }) => {
  const [verTodo, setVerTodo] = useState(false);
  const TOPE = 60;

  if (fuente.estado === 'pendiente') {
    return (
      <div className="space-y-1.5">
        <h4 className="text-xs font-bold text-gray-400">{fuente.label}</h4>
        <p className="text-[11px] text-gray-600">{fuente.nota || 'Sin fuente conectada.'}</p>
      </div>
    );
  }

  if (fuente.estado === 'error') {
    return (
      <div className="space-y-1.5">
        <h4 className="text-xs font-bold text-gray-400">{fuente.label}</h4>
        <p className="text-[11px] text-red-400">{fuente.nota}</p>
      </div>
    );
  }

  const filas = verTodo ? fuente.filas : fuente.filas.slice(0, TOPE);
  const ocultas = fuente.filas.length - filas.length;

  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <h4 className="text-xs font-bold text-gray-300">
          {fuente.label}
          <span className="ml-2 text-[11px] font-normal text-gray-500">
            {fuente.filas.length} {fuente.granularidad === 'movimiento' ? 'movimiento(s)' : 'día(s)'}
          </span>
        </h4>
      </div>
      {fuente.nota && <p className="text-[11px] text-gray-600 max-w-3xl">{fuente.nota}</p>}

      {!fuente.filas.length ? (
        <p className="text-[11px] text-gray-600">Sin registros en el período.</p>
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border border-gray-800 max-h-[420px] overflow-y-auto">
            <table className="w-full text-xs border-collapse">
              <thead className="sticky top-0 z-10">
                <tr className="bg-[#232733] text-gray-400">
                  {fuente.columnas.map((col) => (
                    <th
                      key={col.key}
                      className={`font-bold px-3 py-2 whitespace-nowrap ${
                        col.tipo === 'importe' ? 'text-right' : 'text-left'
                      }`}
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filas.map((f, i) => (
                  <tr key={i} className="border-t border-gray-800/60 hover:bg-white/[0.02]">
                    {fuente.columnas.map((col) => (
                      <td
                        key={col.key}
                        className={`px-3 py-1.5 whitespace-nowrap ${
                          col.tipo === 'importe'
                            ? 'text-right tabular-nums text-gray-200'
                            : 'text-gray-400'
                        } ${col.key === 'descripcion' ? 'max-w-[320px] truncate' : ''}`}
                        title={col.tipo === 'texto' ? (f[col.key] || '') : undefined}
                      >
                        {celda(col, f)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
              <tfoot className="sticky bottom-0">
                <tr className="bg-[#232733] border-t-2 border-gray-700">
                  <td
                    className="px-3 py-2 font-black text-gray-300 whitespace-nowrap"
                    colSpan={fuente.columnas.length - 1}
                  >
                    Total del período{ocultas > 0 ? ` (incluye las ${ocultas} filas no listadas)` : ''}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-black text-white whitespace-nowrap">
                    {fmtMoney(fuente.total)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {ocultas > 0 && (
            <button
              onClick={() => setVerTodo(true)}
              className="text-[11px] font-bold text-[#00a1df] hover:underline"
            >
              Ver las {ocultas} filas restantes
            </button>
          )}
        </>
      )}
    </div>
  );
};

// Cada grupo no sumable tiene su motivo, y no es el mismo.
const NOTA_TABLA = {
  compras: 'Las órdenes de compra y las facturas no se suman entre sí: la OC de hoy se factura después.',
  tesoreria: 'Cada tabla tiene su propio total: lo que entró por un lado y lo que salió por el otro. No se restan entre sí.',
};

const TablaGrupo = ({ grupo }) => (
  <div className="space-y-4">
    <h3 className="text-[13px] font-black text-white">{grupo.titulo}</h3>
    {NOTA_TABLA[grupo.key] && (
      <p className="text-[11px] text-gray-600 -mt-2">{NOTA_TABLA[grupo.key]}</p>
    )}
    {grupo.conceptos.map((c) => (
      <div key={c.key} className="space-y-3 pl-3 border-l border-gray-800">
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <h4 className="text-xs font-black uppercase tracking-wide text-gray-500">{c.label}</h4>
          {c.total !== null && c.total !== undefined && (
            <span className="text-xs text-gray-400">
              Total del concepto:{' '}
              <span className="font-black text-white tabular-nums">{fmtMoney(c.total)}</span>
            </span>
          )}
        </div>
        {c.fuentes.map((f) => <TablaFuente key={f.key} fuente={f} />)}
      </div>
    ))}
  </div>
);

const Tablas = ({ fecha, token, ocupado }) => {
  const [abierto, setAbierto] = useState(false);
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);

  // Carga perezosa: no se pide nada hasta abrirlo. Si está abierto y cambia la
  // fecha, se vuelve a pedir para no mostrar el mes anterior.
  useEffect(() => {
    if (!abierto) return;
    let vigente = true;
    (async () => {
      setCargando(true); setError(null);
      try {
        const res = await fetch(`${BASE}/tablas?fecha=${encodeURIComponent(fecha)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const body = await res.json();
        if (!res.ok) throw new Error(body.detail || `Error ${res.status}`);
        if (vigente) setData(body);
      } catch (err) {
        if (vigente) setError(err.message);
      } finally {
        if (vigente) setCargando(false);
      }
    })();
    return () => { vigente = false; };
  }, [abierto, fecha, token]);

  return (
    <section className="bg-[#1A1D27] rounded-xl border border-gray-800 shadow-xl overflow-hidden">
      <button
        onClick={() => setAbierto((v) => !v)}
        className="w-full flex items-center gap-2.5 px-5 py-4 text-left hover:bg-white/[0.02] transition-colors"
        aria-expanded={abierto}
      >
        <ChevronRight
          className={`w-4 h-4 text-gray-500 transition-transform ${abierto ? 'rotate-90' : ''}`}
        />
        <Table2 className="w-4 h-4 text-[#00a1df]" />
        <span className="text-sm font-black text-white">Tablas</span>
        <span className="text-[11px] text-gray-500">
          Los registros que componen cada total
        </span>
      </button>

      {abierto && (
        <div className="px-5 pb-5 space-y-8 border-t border-gray-800 pt-5">
          {(cargando || ocupado) ? (
            <p className="flex items-center gap-2 text-xs text-[#00a1df] font-semibold">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Cargando el detalle del {fmtFecha(fecha)}…
            </p>
          ) : data && (
            <p className="text-[11px] text-gray-500">
              Del {fmtFecha(data.desde)} al {fmtFecha(data.hasta)} — el mismo período que las tarjetas.
            </p>
          )}
          {error && (
            <p className="flex items-center gap-2 text-xs text-red-400">
              <AlertCircle className="w-3.5 h-3.5" /> {error}
            </p>
          )}
          <div
            className={`space-y-8 transition-opacity duration-200 ${
              (cargando || ocupado) ? 'opacity-40 pointer-events-none select-none' : 'opacity-100'
            }`}
            aria-busy={cargando || ocupado}
          >
            {data?.grupos?.map((g) => <TablaGrupo key={g.key} grupo={g} />)}
          </div>
        </div>
      )}
    </section>
  );
};

// ── Conciliadora del pendiente de cobro ──
//
// Contesta una sola pregunta: el pendiente de cobro que muestra la cartera, ¿es
// el que debería ser dado lo que se vendió y lo que se cobró en el período?
//
//   esperado = inicio + venta − cobros − retenciones + saldos a favor − compensaciones
//
// Los dos selectores mueven LOS DOS lados a la vez. Eso no es una comodidad: si
// se saca lo judicial (o las proformas) de la cartera y no de la venta, la
// diferencia se infla en todo lo que haya de eso sin cobrar y no cierra nunca,
// por construcción. Por eso son un solo control y no uno por lado.
const primerDiaDelMes = () => `${hoyAR().slice(0, 7)}-01`;

const ORIGEN_INICIO = {
  foto: 'de la foto de la cartera del día anterior',
  parametro: 'cargado a mano',
  cero: 'sin foto previa: arranca en 0',
};

const TerminoConciliacion = ({ termino, desde, hasta, token }) => {
  const [abierto, setAbierto] = useState(false);
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const desplegable = !!termino.concepto;

  useEffect(() => {
    if (!abierto || data) return;
    let vigente = true;
    (async () => {
      setCargando(true); setError(null);
      try {
        const res = await fetch(
          `${BASE}/tablas?desde=${encodeURIComponent(desde)}&hasta=${encodeURIComponent(hasta)}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const body = await res.json();
        if (!res.ok) throw new Error(body.detail || `Error ${res.status}`);
        if (vigente) setData(body);
      } catch (err) {
        if (vigente) setError(err.message);
      } finally {
        if (vigente) setCargando(false);
      }
    })();
    return () => { vigente = false; };
  }, [abierto, data, desde, hasta, token]);

  // El efectivo del término, no su valor absoluto: un saldo a favor que BAJÓ
  // suma con signo negativo, y mostrar "+ −$30" no se entiende.
  const efectivo = termino.signo * termino.importe;

  // La tabla del concepto de este término, dentro del rango de la conciliación.
  const concepto = useMemo(() => {
    if (!data) return null;
    for (const g of data.grupos || []) {
      const c = (g.conceptos || []).find((x) => x.key === termino.concepto);
      if (c) return c;
    }
    return null;
  }, [data, termino.concepto]);

  // Las retenciones y los cobros comparten concepto en el panel, así que al
  // desplegar se muestran las tres fuentes y hay que decir cuál es cuál.
  const fuentes = concepto?.fuentes || [];

  return (
    <div>
      <div className="flex items-baseline gap-3 py-1.5">
        {desplegable ? (
          <button
            onClick={() => setAbierto((v) => !v)}
            className="flex items-baseline gap-1.5 text-left hover:text-white transition-colors min-w-0"
            aria-expanded={abierto}
          >
            <ChevronRight className={`w-3 h-3 shrink-0 text-gray-600 transition-transform ${abierto ? 'rotate-90' : ''}`} />
            <span className="text-xs text-gray-300 truncate">{termino.label}</span>
          </button>
        ) : (
          <span className="text-xs text-gray-300 pl-[18px]">{termino.label}</span>
        )}
        <span className={`ml-auto text-sm font-bold tabular-nums shrink-0 ${
          efectivo < 0 ? 'text-red-400/90' : 'text-emerald-400/90'
        }`}>
          {efectivo < 0 ? '−' : '+'} {fmtMoney(Math.abs(efectivo))}
        </span>
      </div>

      {termino.detalle && (
        <p className="text-[10px] text-gray-600 leading-snug pl-[18px] pb-1 max-w-2xl">
          {termino.detalle}
        </p>
      )}
      {!termino.medido && (
        <p className="text-[10px] text-yellow-500/80 leading-snug pl-[18px] pb-1">
          {termino.avisos?.join(' ') || 'Este término no está medido.'}
        </p>
      )}

      {abierto && (
        <div className="mb-3 ml-4 pl-3 border-l border-gray-800 space-y-4">
          {cargando && (
            <p className="flex items-center gap-2 text-[11px] text-[#00a1df] font-semibold">
              <Loader2 className="w-3 h-3 animate-spin" /> Buscando los registros…
            </p>
          )}
          {error && (
            <p className="flex items-center gap-2 text-[11px] text-red-400">
              <AlertCircle className="w-3 h-3" /> {error}
            </p>
          )}
          {data && !cargando && !fuentes.length && (
            <p className="text-[11px] text-gray-500">
              No hay registros guardados de este concepto en el rango.
            </p>
          )}
          {fuentes.map((f) => <TablaFuente key={f.key} fuente={f} />)}
        </div>
      )}
    </div>
  );
};

const Conciliadora = ({ token }) => {
  const [abierto, setAbierto] = useState(false);
  const [desde, setDesde] = useState(primerDiaDelMes);
  const [hasta, setHasta] = useState(hoyAR);
  // Vacío = automático (la foto del día anterior). Es un string y no un número
  // para poder distinguir "vacío" de "cero", que acá significan cosas opuestas.
  const [inicio, setInicio] = useState('');
  const [judicial, setJudicial] = useState(false);
  const [proformas, setProformas] = useState(true);
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);

  const pedido = useRef(0);

  useEffect(() => {
    if (!abierto) return;
    const mio = ++pedido.current;
    (async () => {
      setCargando(true); setError(null);
      try {
        const q = new URLSearchParams({
          desde, hasta,
          incluir_judicial: String(judicial),
          incluir_proformas: String(proformas),
        });
        if (inicio.trim() !== '') q.set('inicio', inicio.trim());
        const res = await fetch(`${BASE}/conciliacion?${q}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const body = await res.json();
        if (mio !== pedido.current) return;
        if (!res.ok) throw new Error(body.detail || `Error ${res.status}`);
        setData(body);
      } catch (err) {
        if (mio !== pedido.current) return;
        setError(err.message);
      } finally {
        if (mio === pedido.current) setCargando(false);
      }
    })();
  }, [abierto, desde, hasta, inicio, judicial, proformas, token]);

  const dif = data?.diferencia;
  // El umbral es de presentación, no de contabilidad: por debajo de un peso la
  // diferencia es redondeo y pintarla de rojo haría perder el tiempo.
  const cierra = dif && Math.abs(dif.importe) < 1;

  return (
    <section className="bg-[#1A1D27] rounded-xl border border-gray-800 shadow-xl overflow-hidden">
      <button
        onClick={() => setAbierto((v) => !v)}
        className="w-full flex items-center gap-2.5 px-5 py-4 text-left hover:bg-white/[0.02] transition-colors"
        aria-expanded={abierto}
      >
        <ChevronRight className={`w-4 h-4 text-gray-500 transition-transform ${abierto ? 'rotate-90' : ''}`} />
        <Scale className="w-4 h-4 text-[#00a1df]" />
        <span className="text-sm font-black text-white">Conciliadora del pendiente de cobro</span>
        <span className="text-[11px] text-gray-500">
          Lo que debería faltar cobrar contra lo que la cartera dice que falta
        </span>
      </button>

      {abierto && (
        <div className="px-5 pb-5 border-t border-gray-800 pt-5 space-y-5">

          {/* Controles */}
          <div className="flex items-end gap-3 flex-wrap">
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-bold uppercase text-gray-500">Desde</span>
              <input type="date" value={desde} max={hasta}
                     onChange={(e) => e.target.value && setDesde(e.target.value)}
                     className="bg-[#0F1117] border border-gray-700 rounded-lg px-3 py-1.5 text-xs text-gray-200 focus:outline-none focus:border-[#00a1df]" />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-bold uppercase text-gray-500">Hasta</span>
              <input type="date" value={hasta} max={hoyAR()} min={desde}
                     onChange={(e) => e.target.value && setHasta(e.target.value)}
                     className="bg-[#0F1117] border border-gray-700 rounded-lg px-3 py-1.5 text-xs text-gray-200 focus:outline-none focus:border-[#00a1df]" />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-bold uppercase text-gray-500">Pendiente al inicio</span>
              <input type="number" step="0.01" value={inicio} placeholder="automático"
                     onChange={(e) => setInicio(e.target.value)}
                     className="bg-[#0F1117] border border-gray-700 rounded-lg px-3 py-1.5 text-xs text-gray-200 w-40 focus:outline-none focus:border-[#00a1df]" />
            </label>
            <label className="flex items-center gap-2 text-xs text-gray-300 pb-1.5">
              <input type="checkbox" checked={judicial} onChange={(e) => setJudicial(e.target.checked)}
                     className="accent-[#00a1df]" />
              Incluir gestión judicial
            </label>
            <label className="flex items-center gap-2 text-xs text-gray-300 pb-1.5">
              <input type="checkbox" checked={proformas} onChange={(e) => setProformas(e.target.checked)}
                     className="accent-[#00a1df]" />
              Incluir X / proformas
            </label>
          </div>
          <p className="text-[10px] text-gray-600 leading-snug max-w-3xl -mt-3">
            Los dos selectores mueven los dos lados a la vez: sacan las facturas de la venta
            <em> y</em> de la cartera. Moverlos de un solo lado infla la diferencia en todo lo
            que haya de eso sin cobrar.
          </p>

          {cargando && (
            <p className="flex items-center gap-2 text-xs text-[#00a1df] font-semibold">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Conciliando…
            </p>
          )}
          {error && (
            <p className="flex items-center gap-2 text-xs text-red-400">
              <AlertCircle className="w-3.5 h-3.5" /> {error}
            </p>
          )}

          {data && data.hay_datos === false && (
            <div className="space-y-1">
              {data.avisos?.map((a) => (
                <p key={a} className="text-xs text-yellow-500/90 leading-snug">{a}</p>
              ))}
            </div>
          )}

          {data?.hay_datos && (
            <div className={`space-y-5 transition-opacity ${cargando ? 'opacity-40' : ''}`}>

              {data.avisos?.length > 0 && (
                <div className="space-y-1 bg-yellow-500/[0.06] border border-yellow-500/20 rounded-lg p-3">
                  {data.avisos.map((a) => (
                    <p key={a} className="text-[11px] text-yellow-500/90 leading-snug">{a}</p>
                  ))}
                </div>
              )}

              {/* El esperado, término por término */}
              <div className="rounded-lg border border-gray-800 p-4 space-y-1">
                <div className="flex items-baseline gap-3 pb-2 border-b border-gray-800">
                  <span className="text-xs text-gray-300 pl-[18px]">{data.inicio.label}</span>
                  <span className="ml-auto text-sm font-bold text-gray-200 tabular-nums">
                    {fmtMoney(data.inicio.importe)}
                  </span>
                </div>
                <p className="text-[10px] text-gray-600 pl-[18px] pb-1">
                  {ORIGEN_INICIO[data.inicio.origen] || data.inicio.origen}
                  {data.inicio.corte ? ` (${fmtFecha(data.inicio.corte)})` : ''}
                </p>

                {data.terminos.map((tr) => (
                  <TerminoConciliacion key={tr.key} termino={tr}
                                       desde={data.desde} hasta={data.hasta} token={token} />
                ))}

                <div className="flex items-baseline gap-3 pt-3 mt-2 border-t border-gray-800">
                  <span className="text-xs font-bold text-white">Pendiente esperado</span>
                  <span className="ml-auto text-lg font-black text-white tabular-nums">
                    {fmtMoney(data.esperado.importe)}
                  </span>
                </div>
                {!data.esperado.completo && (
                  <p className="text-[10px] text-yellow-500/80 leading-snug">
                    Falta medir: {data.esperado.sin_medir.join(', ')}. El esperado está incompleto,
                    así que la diferencia todavía no es una conclusión.
                  </p>
                )}
              </div>

              {/* El real y la diferencia */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="rounded-lg bg-white/[0.03] border border-gray-800 p-4">
                  <p className="text-[10px] font-bold uppercase text-gray-400 mb-1">
                    {data.real.label}
                  </p>
                  <p className="text-xl font-black text-white tabular-nums leading-none">
                    {fmtMoney(data.real.importe)}
                  </p>
                  <p className="text-[10px] text-gray-500 mt-2 leading-snug">
                    Al {fmtFecha(data.real.corte)}: {fmtCorto(data.real.deuda.importe)} en{' '}
                    {data.real.deuda.facturas} facturas y {fmtCorto(data.real.cheques.importe)} en{' '}
                    {data.real.cheques.cheques} cheques en cartera.
                  </p>
                  {Object.entries(data.real.excluidas || {}).map(([k, e]) => (
                    e?.cantidad > 0 && (
                      <p key={k} className="text-[10px] text-gray-600 mt-1">
                        Sin {k === 'judicial' ? 'gestión judicial' : 'proformas'}:{' '}
                        {fmtCorto(e.importe)} en {e.cantidad} fila(s) quedaron afuera de los dos lados.
                      </p>
                    )
                  ))}
                </div>

                <div className={`rounded-lg border p-4 ${
                  cierra
                    ? 'bg-emerald-500/[0.07] border-emerald-500/30'
                    : 'bg-red-500/[0.07] border-red-500/30'
                }`}>
                  <p className="text-[10px] font-bold uppercase text-gray-400 mb-1">Diferencia</p>
                  <p className={`text-xl font-black tabular-nums leading-none ${
                    cierra ? 'text-emerald-400' : 'text-red-400'
                  }`}>
                    {fmtMoney(dif.importe)}
                    {dif.porcentaje != null && (
                      <span className="text-xs font-bold ml-2">({fmtPct(dif.porcentaje)})</span>
                    )}
                  </p>
                  <p className="text-[10px] text-gray-500 mt-2 leading-snug">
                    {cierra
                      ? 'Cierra: la cartera es exactamente la que explican la venta y los cobros del período.'
                      : dif.importe > 0
                        ? 'La cartera tiene MÁS pendiente del que explican la venta y los cobros: sobra deuda que ya debería haberse cancelado, o faltan cobros cargados.'
                        : 'La cartera tiene MENOS pendiente del que explican la venta y los cobros: se canceló deuda que no aparece como cobro, o falta venta cargada.'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

const PanelDiarioPage = () => {
  const { token } = useAuth();

  const [fecha, setFecha] = useState(hoyAR);
  const [meses, setMeses] = useState([]);
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [refrescando, setRefrescando] = useState(false);
  const [error, setError] = useState(null);

  // Cada pedido lleva número. Si mientras carga una fecha se elige otra, la
  // respuesta vieja se descarta: sin esto, cambiar de fecha rápido puede dejar
  // en pantalla los números de la fecha anterior.
  const pedido = useRef(0);

  const cargar = useCallback(async (f, forzar = false) => {
    const mio = ++pedido.current;
    if (forzar) setRefrescando(true); else setCargando(true);
    setError(null);
    try {
      const url = `${BASE}${forzar ? '/refrescar' : ''}?fecha=${encodeURIComponent(f)}`;
      const res = await fetch(url, {
        method: forzar ? 'POST' : 'GET',
        headers: { Authorization: `Bearer ${token}` },
      });
      const body = await res.json();
      if (mio !== pedido.current) return;        // llegó tarde: ya hay otro pedido
      if (!res.ok) throw new Error(body.detail || `Error ${res.status}`);
      setData(body);
      if (forzar) toast.success('Panel actualizado');
      const fallidos = (body.conceptos || []).filter((c) => c.estado === 'error');
      if (fallidos.length) {
        toast.warning(`${fallidos.length} concepto(s) no se pudieron leer de YiQi.`);
      }
    } catch (err) {
      if (mio !== pedido.current) return;
      setError(err.message);
      toast.error('Error al cargar el panel: ' + err.message);
    } finally {
      if (mio === pedido.current) {
        setCargando(false);
        setRefrescando(false);
      }
    }
  }, [token]);

  useEffect(() => { cargar(fecha); }, [fecha, cargar]);

  // Meses que ya tienen histórico guardado, para el selector.
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${BASE}/meses`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) return;
        const body = await res.json();
        setMeses(body.meses || []);
      } catch { /* sin selector de meses; el calendario sigue funcionando */ }
    })();
  }, [token, data]);

  const porGrupo = useMemo(() => {
    const out = {};
    (data?.conceptos || []).forEach((c) => {
      (out[c.grupo] = out[c.grupo] || []).push(c);
    });
    return out;
  }, [data]);

  const esHoy = fecha === hoyAR();
  const ocupado = cargando || refrescando;
  // Mientras carga, lo que se ve en las tarjetas todavía es de la fecha anterior.
  const datosDeOtraFecha = !!data && data.fecha !== fecha;

  // El aviso de histórico solo tiene sentido si TODAS las fuentes con dato
  // vienen de Mongo. Con que una sola siga viva, el mes está en la ventana de
  // las smarties y el cartel confundiría más de lo que aclara.
  const fuentesConDato = (data?.conceptos || [])
    .flatMap((c) => c.componentes || [])
    .filter((k) => k.estado === 'ok');
  const desdeHistorico =
    fuentesConDato.length > 0 &&
    fuentesConDato.every((k) => k.fuente === 'histórico local');

  return (
    <div className="p-8 space-y-8">

      {/* Encabezado */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-black text-white flex items-center gap-2.5">
            <Gauge className="w-6 h-6 text-[#00a1df]" />
            Panel Diario
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Acumulado del mes y movimiento del día, contra el mismo período del mes anterior.
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {meses.length > 1 && (
            <select
              value={fecha.slice(0, 7)}
              onChange={(e) => setFecha(ultimoDiaDelMes(e.target.value))}
              className="bg-[#0F1117] border border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-[#00a1df]"
            >
              {!meses.includes(fecha.slice(0, 7)) && (
                <option value={fecha.slice(0, 7)}>{fmtMes(fecha.slice(0, 7))}</option>
              )}
              {meses.map((m) => <option key={m} value={m}>{fmtMes(m)}</option>)}
            </select>
          )}
          <input
            type="date"
            value={fecha}
            max={hoyAR()}
            onChange={(e) => e.target.value && setFecha(e.target.value)}
            className="bg-[#0F1117] border border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-[#00a1df]"
          />
          {!esHoy && (
            <button
              onClick={() => setFecha(hoyAR())}
              className="px-3 py-2 rounded-lg text-sm font-semibold text-gray-300 border border-gray-700 hover:border-gray-500"
            >
              Hoy
            </button>
          )}
          <button
            onClick={() => cargar(fecha, true)}
            disabled={ocupado}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold bg-[#00a1df] text-white hover:bg-[#0090c8] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {ocupado
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <RefreshCw className="w-4 h-4" />}
            {ocupado ? 'Actualizando…' : 'Actualizar'}
          </button>
        </div>
      </div>

      {/* Barra de progreso: la señal de que hay un pedido en curso. */}
      <div className="h-0.5 -mt-5 rounded-full bg-gray-800/60 overflow-hidden" aria-hidden="true">
        {ocupado && <div className="barra-indeterminada h-full w-full" />}
      </div>

      {/* Estado de la lectura */}
      {(data || ocupado) && (
        <div className="flex items-center gap-4 flex-wrap text-[11px] text-gray-500 -mt-6">
          {ocupado ? (
            <span className="flex items-center gap-1.5 text-[#00a1df] font-semibold">
              <Loader2 className="w-3 h-3 animate-spin" />
              {refrescando
                ? `Actualizando ${fmtFecha(fecha)} contra YiQi…`
                : `Buscando los datos del ${fmtFecha(fecha)}…`}
            </span>
          ) : (
            <span className="flex items-center gap-1.5">
              <Clock className="w-3 h-3" />
              Actualizado {fmtHora(data.actualizado_at)}
              {data.origen === 'cache' && ' (cacheado)'}
            </span>
          )}

          {/* Lo que se ve no es de la fecha pedida: decirlo, no dejarlo adivinar. */}
          {datosDeOtraFecha && (
            <span className="flex items-center gap-1.5 text-yellow-500/90 font-semibold">
              <AlertCircle className="w-3 h-3" />
              En pantalla todavía: {fmtFecha(data.fecha)}
            </span>
          )}
          {data && !ocupado && (
            <>
              <span>
                Acumulado del {fmtFecha(`${data.mes}-01`)} al {fmtFecha(data.fecha)}
              </span>
              <span>
                Comparado contra {fmtFecha(data.periodo_comparado?.desde)} – {fmtFecha(data.periodo_comparado?.hasta)}
              </span>
            </>
          )}
          {data && !ocupado && desdeHistorico && (
            <span className="text-yellow-500/80">
              Este mes ya no lo devuelven las smarties: los datos salen del histórico guardado en la app.
            </span>
          )}
        </div>
      )}

      {error && (
        <div className="flex items-center gap-3 bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-sm text-red-400">
          <AlertCircle className="w-5 h-5 shrink-0" />
          {error}
        </div>
      )}

      {/* Grupos. Mientras hay un pedido en curso se atenúan y se bloquean: los
          números que se ven son los de la lectura anterior. */}
      {cargando && !data ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {[0, 1, 2, 3, 4].map((i) => <Esqueleto key={i} />)}
        </div>
      ) : (
        <div
          className={`space-y-8 transition-opacity duration-200 ${
            ocupado ? 'opacity-40 pointer-events-none select-none' : 'opacity-100'
          }`}
          aria-busy={ocupado}
        >
        {GRUPOS.map((g) => {
          const conceptos = porGrupo[g.key] || [];
          if (!conceptos.length) return null;
          return (
            <section key={g.key} className="space-y-3">
              <div>
                <h2 className="text-sm font-black uppercase tracking-wider text-gray-400">{g.titulo}</h2>
                {g.nota && <p className="text-[11px] text-gray-600 mt-1 max-w-3xl">{g.nota}</p>}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                {conceptos.map((c) => <Tarjeta key={c.key} concepto={c} />)}
                {g.key === 'cobranzas' && <TarjetaCartera cartera={data?.cartera} token={token} />}
              </div>
            </section>
          );
        })}
        </div>
      )}

      {/* Verificación: cerrado por defecto, se abre solo si hace falta */}
      {data && <Tablas fecha={fecha} token={token} ocupado={ocupado} />}

      {/* Conciliadora: también cerrada por defecto. Es una pregunta puntual, no
          un número de todos los días. */}
      {data && <Conciliadora token={token} />}
    </div>
  );
};

export default PanelDiarioPage;
